import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Card, CardContent, Typography, CircularProgress, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, Button, Divider
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';
import { parseUtc } from '../../utils/time';


const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatTime(isoStr) {
    if (!isoStr) return '--';
    return parseUtc(isoStr).toLocaleTimeString('en-AU', {
        hour: '2-digit',
        minute: '2-digit',
    });
}

function formatDateLabel(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    const dayName = dayNames[(d.getDay() + 6) % 7];
    return `${dayName} ${d.getDate()}/${d.getMonth() + 1}`;
}

export default function WorkerTimesheet() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [weekData, setWeekData] = useState(null);
    const [weekOffset, setWeekOffset] = useState(0);

    const employeeId = user?.employee_id;

    const fetchTimesheet = useCallback(async () => {
        if (!employeeId) {
            setError('No employee record linked to your account.');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const today = new Date();
            const monday = new Date(today);
            monday.setDate(today.getDate() - today.getDay() + 1 + weekOffset * 7);
            const startDate = monday.toLocaleDateString('en-CA');

            const resp = await api.get(`/worker/clock/weekly`, {
                params: { employee_id: employeeId, start_date: startDate },
            });
            setWeekData(resp.data);
            setError(null);
        } catch (err) {
            const msg = err.response?.data?.detail || err.message || 'Failed to load timesheet';
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [employeeId, weekOffset]);

    useEffect(() => {
        fetchTimesheet();
    }, [fetchTimesheet]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!weekData) {
        return <Alert severity="info">No timesheet data available.</Alert>;
    }

    return (
        <Box sx={{ p: 2 }}>
            {/* Week navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Button
                    size="small"
                    onClick={() => setWeekOffset(w => w - 1)}
                    disabled={weekOffset <= -4}
                >
                    ← Previous Week
                </Button>
                <Typography variant="subtitle1" fontWeight="bold">
                    {weekData.week_start} to {weekData.week_end}
                </Typography>
                <Button
                    size="small"
                    onClick={() => setWeekOffset(w => w + 1)}
                    disabled={weekOffset >= 0}
                >
                    Next Week →
                </Button>
            </Box>

            {/* Total hours summary */}
            <Card sx={{ mb: 2, bgcolor: 'primary.main', color: 'white' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="h6" component="div">
                        Total: {weekData.total_hours} hrs
                    </Typography>
                </CardContent>
            </Card>

            {/* Daily breakdown */}
            {weekData.days.length === 0 ? (
                <Alert severity="info">No clock events this week.</Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Day</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Hours</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Events</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {weekData.days
                                .sort((a, b) => a.date.localeCompare(b.date))
                                .map((day) => (
                                    <TableRow key={day.date}>
                                        <TableCell>{formatDateLabel(day.date)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={`${day.hours}h`}
                                                size="small"
                                                color={day.hours > 0 ? 'success' : 'default'}
                                                variant={day.hours > 0 ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {day.events.map((ev, i) => (
                                                <Typography
                                                    key={ev.id || i}
                                                    variant="caption"
                                                    component="div"
                                                    sx={{ lineHeight: 1.4 }}
                                                >
                                                    {formatTime(ev.timestamp)} — {ev.event_type.replace(/_/g, ' ')}
                                                    {ev.notes ? ` (${ev.notes})` : ''}
                                                </Typography>
                                            ))}
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Divider sx={{ my: 2 }} />

            <Button
                variant="outlined"
                size="small"
                onClick={fetchTimesheet}
                fullWidth
            >
                Refresh
            </Button>
        </Box>
    );
}
